import { calculateCockpitGrowth, calculateCockpitSobGrowth } from '../calculateCockpitGrowth'
import { getPlanningQuality, weightedAverage } from '../planningQuality'
import {
  IRevenueAndSobHierarchyFigures,
  IRevenueAndSobHierarchyRow,
  TRevenueAndSobHierarchyLevel,
} from '../revenueAndSobHierarchy'

import { IPlanQualityHierarchyMetrics, IPlanQualityHierarchyRow, TPlanQualityPillar } from './types'

/** The levels at which a node's scope is restricted to a single category. */
const CATEGORY_SCOPED_LEVELS: ReadonlySet<TRevenueAndSobHierarchyLevel> = new Set([
  'category',
  'subCategory',
  'l3Category',
])

/** Which nodes may use the rate the API published for their own rows. `marketGrowth` reaches a node
 *  through `findGrowthValue` — "the first non-null row wins", since the backend repeats one
 *  market-wide rate across a category's rows. For a node scoped to one category that is
 *  unambiguously that category's target. Above the category level (the Total root, a Geo node, an
 *  Offering node, a partner leaf hanging above the product axis) the winner is whichever category
 *  happened to sort first, which means nothing — those nodes take the weighted roll-up instead. */
const getCategoryScopedRowIds = (rows: IRevenueAndSobHierarchyRow[]): Map<string, boolean> => {
  const rowById = new Map(rows.map((row) => [row.rowId, row]))
  const isScoped = new Map<string, boolean>()

  rows.forEach((row) => {
    const chain: string[] = []
    let cursor: IRevenueAndSobHierarchyRow | undefined = row
    let result = false

    while (cursor) {
      const cached = isScoped.get(cursor.rowId)

      if (cached !== undefined) {
        result = cached
        break
      }

      chain.push(cursor.rowId)

      if (CATEGORY_SCOPED_LEVELS.has(cursor.level)) {
        result = true
        break
      }

      cursor = cursor.parentRowId === null ? undefined : rowById.get(cursor.parentRowId)
    }

    chain.forEach((rowId) => isScoped.set(rowId, result))
  })

  return isScoped
}

interface IBuildMetricsParams {
  figures: IRevenueAndSobHierarchyFigures
  /** The node's market benchmark, already resolved — its own published rate where that is
   *  trustworthy, the weighted roll-up of its categories otherwise. */
  marketTarget: number | null
  /** SOB is a share, so its change is measured in percentage points and has no money behind it;
   *  the financial measure is a percentage change whose amount fills the bracket. */
  isSob: boolean
}

const buildMetrics = ({
  figures,
  marketTarget,
  isSob,
}: IBuildMetricsParams): IPlanQualityHierarchyMetrics => {
  const { baseline, plan } = figures
  const yoy = isSob
    ? calculateCockpitSobGrowth({ value: plan, baseline })
    : calculateCockpitGrowth({ value: plan, baseline })

  return {
    baseline,
    plan,
    yoy,
    yoyAbsolute: isSob || plan === null ? null : plan - (baseline ?? 0),
    marketTarget,
    marketTargetAbsolute:
      isSob || marketTarget === null || !baseline ? null : (baseline * marketTarget) / 100,
    /** The same scoring the flat category table applies, node for node — `getPlanningQuality` is
     *  the cockpit's own rule, so a Category row here reads as it does there. */
    score: getPlanningQuality(yoy, marketTarget),
    quota: figures.quota,
  }
}

/** Turns the Revenue & SOB hierarchy into the Plan Quality tree the manager cockpit renders: every
 *  node keeps its own baseline and plan, and gains a planned YoY, a market benchmark and a score for
 *  each of the two pillars.
 *
 *  Planned YoY falls straight out of the node's own figures, which `buildFigures` has already summed
 *  from that node's rows — two partners at 1000→1050 and 5000→5400 give +5.0% and +8.0% on their own
 *  rows and +7.5% on the node above them. The market benchmark rolls up as a baseline-weighted mean
 *  of the *nearest* category-scoped descendants: weighting by the nearest rather than by all of them
 *  keeps a category from counting twice through its own children.
 *
 *  Every node then scores from its own two figures, so a row's cells read left to right as working
 *  and result. */
export const getPlanQualityHierarchyRows = (
  rows: IRevenueAndSobHierarchyRow[]
): IPlanQualityHierarchyRow[] => {
  const isScoped = getCategoryScopedRowIds(rows)
  const childIdsByParentId = new Map<string, string[]>()

  rows.forEach(({ rowId, parentRowId }) => {
    if (parentRowId === null) {
      return
    }

    childIdsByParentId.set(parentRowId, [...(childIdsByParentId.get(parentRowId) ?? []), rowId])
  })

  /** What a scoped node contributes to an ancestor's roll-up: the rate the API published for it, and
   *  the revenue both pillars weight that rate by. Only scoped nodes are ever on a frontier. */
  const frontierEntryByRowId = new Map<
    string,
    { targets: Record<TPlanQualityPillar, number | null>; weight: number | null }
  >()

  rows.forEach(({ rowId, sales, sob }) => {
    if (!isScoped.get(rowId)) {
      return
    }

    frontierEntryByRowId.set(rowId, {
      targets: { financial: sales.marketGrowth, sob: sob.marketGrowth },
      weight: sales.baseline,
    })
  })

  /** The scoped nodes a given node rolls up: itself once it is scoped, otherwise the same question
   *  asked of its children. Memoised because every ancestor of a node asks it again. */
  const frontierIdsByRowId = new Map<string, string[]>()
  const getFrontierIds = (rowId: string): string[] => {
    const cached = frontierIdsByRowId.get(rowId)

    if (cached) {
      return cached
    }

    const frontierIds = isScoped.get(rowId)
      ? [rowId]
      : (childIdsByParentId.get(rowId) ?? []).flatMap(getFrontierIds)

    frontierIdsByRowId.set(rowId, frontierIds)

    return frontierIds
  }

  return rows.map(({ sales, sob, ...node }) => {
    const isOwnTargetTrustworthy = isScoped.get(node.rowId) === true
    const frontier = isOwnTargetTrustworthy
      ? []
      : getFrontierIds(node.rowId).flatMap((rowId) => frontierEntryByRowId.get(rowId) ?? [])

    const getMarketTarget = (pillar: TPlanQualityPillar, own: number | null) =>
      isOwnTargetTrustworthy
        ? own
        : weightedAverage(
            frontier.map(({ targets, weight }) => ({ value: targets[pillar], weight }))
          )

    return {
      ...node,
      financial: buildMetrics({
        figures: sales,
        marketTarget: getMarketTarget('financial', sales.marketGrowth),
        isSob: false,
      }),
      sob: buildMetrics({
        figures: sob,
        marketTarget: getMarketTarget('sob', sob.marketGrowth),
        isSob: true,
      }),
    }
  })
}
